<script setup>
import { watch, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useCookies } from '@vueuse/integrations/useCookies'

import ToastsContainer from '@dogfy/components/widgets/ToastsContainer.vue'
import GridLayoutOverlay from '@dogfy/components/widgets/GridLayoutOverlay.vue'
import CookiesConsent from '@/components/widgets/CookiesConsent.vue'
import AppLoaderPaw from '@dogfy/components/layout/AppLoaderPaw.vue'
import StoragePopup from '@/components/widgets/StoragePopup.vue'

import { isStorageAvailable } from '@dogfy/lib/utils'
import { setI18nValidatorLocale } from '@dogfy/lib/i18n-validators'
import ApiService from '@dogfy/services/apiService'
import endpoints from '@dogfy/services/apiEndpoints'
import config from '@/config'

import firebaseMessaging from '@/firebase'
import { useAccountStore } from '@/stores/account'

const { t, locale: currentLocale, availableLocales, fallbackLocale } = useI18n()

const { key: cookieLocaleKey, ...cookieLocaleOptions } = config.cookies.locale
const cookies = useCookies([cookieLocaleKey])

const route = useRoute()
const router = useRouter()

const { query } = route

const { userCountry } = storeToRefs(useAccountStore())

const currentEnv = import.meta.env.VUE_APP_ENVIRONMENT
const isGridOverlayAllowed = currentEnv !== 'production'

const isStoragePopupVisible = ref(false)

manageAppLocale()

// Request FCM push notification permission.
firebaseMessaging.requestPermission()

function handleStoragesAvailability () {
  const areStoragesAvailable = isStorageAvailable('localStorage') && isStorageAvailable('sessionStorage')
  if (!areStoragesAvailable) isStoragePopupVisible.value = true
}

async function manageAppLocale () {
  // Manages app locale taking into account this priority:
  // 1. User defined country.
  // 2. Lang query param.
  // 3. Locale cookie value.
  // 4. User's browser preferred language.
  // 5. User geocontext.

  // This cannot be done:
  //  const userLang = userCountry.value || query.lang || cookies.get('locale')
  //  if (availableLocales.includes(userLang)) {
  //    currentLocale.value = userLang
  //    return
  //  }
  // Because, if query.lang exists but is not related to an available locale, then locale cookie value should be required
  // and doing like indicated, then the locale cookie is not considered.

  // Clean query params if required.
  const { lang: queryLang, ...newQuery } = query
  if (queryLang) {
    router.replace({ query: newQuery })
  }

  // User defined country.
  if (!!userCountry.value && availableLocales.includes(userCountry.value)) {
    currentLocale.value = userCountry.value
    return
  }

  // Lang query param.
  if (!!queryLang && availableLocales.includes(queryLang)) {
    currentLocale.value = queryLang
    return
  }

  // Locale cookie value.
  const localeCookie = cookies.get('locale')
  if (!!localeCookie && availableLocales.includes(localeCookie)) {
    currentLocale.value = localeCookie
    return
  }

  // User's browser preferred language.
  // Take into account browser preferred languages contain the ISO-639-1 format with the regional identifier and we set locales without the regional identifier.
  // Although vue-i18n can manage the regional idenfier when setting the locale.
  const userPreferredLang = navigator.languages
    .map(lang => lang.split('-')[0])
    .find(lang => availableLocales.includes(lang))

  if (userPreferredLang) {
    currentLocale.value = userPreferredLang
    return
  }

  // User geocontext.
  try {
    const { data: geocontext } = await ApiService.get(endpoints.config.geocontext())
    if (!!geocontext.country && availableLocales.includes(geocontext.country)) {
      currentLocale.value = geocontext.country
      return
    }
  } catch (err) {
    console.error('  !! Could not get geocontext data: ', err.response?.data?.message)
  }

  // If none of the above, then set the fallback locale.
  currentLocale.value = fallbackLocale.value
}

watch(currentLocale, (newLocale) => {
  setI18nValidatorLocale(newLocale)

  // Set locale cookie with the same parameters used in website: 1 year age and sameSite lax.
  cookies.set(cookieLocaleKey, newLocale, { maxAge: cookieLocaleOptions.expires, sameSite: cookieLocaleOptions.sameSite })

  document.documentElement.setAttribute('lang', newLocale)
  document.title = t('app.meta.title')
}, { immediate: true })

watch(userCountry, (newCountry) => {
  if (newCountry) {
    currentLocale.value = newCountry
  }
})

watch(() => route.path, async (currentPath, prevPath) => {
  handleStoragesAvailability()
}, { immediate: true })
</script>

<template>
  <StoragePopup v-model="isStoragePopupVisible" />
  <GridLayoutOverlay v-if="isGridOverlayAllowed" />
  <component :is="$route.meta.layoutComponent">
    <ToastsContainer top-element="header" />
    <RouterView v-slot="{ Component }">
      <Suspense>
        <component :is="Component" />
        <template #fallback>
          <AppLoaderPaw class="app__loader" />
        </template>
      </Suspense>
    </RouterView>
  </component>
</template>

<style lang="scss">
// Render a spinner while app is being mounted.
#app[v-cloak] {
  $spinner-size: 80px;

  content: '';

  position: absolute;

  animation: spin 1.5s linear infinite;

  box-sizing: border-box;
  border: 10px solid $color-neutral-semidark;
  border-radius: 50%;
  width: $spinner-size;
  height: $spinner-size;

  inset: 50%;
  border-top-color: transparent;

  @keyframes spin {
    0% {
      transform: translate(-50%, -50%) rotate(0deg);
    }

    100% {
      transform: translate(-50%, -50%) rotate(360deg);
    }
  }
}

.app {
  &__loader {
    position: absolute;
    top: 50%;
    left: 50%;

    transform: translate(-50%, -50%);

    color: $color-neutral-semidark;
  }
}
</style>
